import React, { useCallback, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
    Switch,
    Route,
    useHistory,
} from "react-router-dom";
import './App.scss';
import 'bootstrap/dist/css/bootstrap.min.css';
import Login from './components/login/login';
import { ProductLineEditor } from './components/ProductLine/ProductLineEditor';
import { NavService, NavPage } from './services/NavService';
import { LoginService } from './services/LoginService';
import { ChainInfoClient, ICabinet, TranslationClient } from './services/WebApiClient';
import { ResetTopic, CabinetService, ResetConfirmer } from './services/CabinetService';
import { DimensionsEditor } from './components/DimensionsEditor/DimensionsEditor';
import { TranslationService } from './services/TranslationService';
import { NotFound } from './components/NotFound';
import DoorTypeEditor from './components/DoorTypeEditor/DoorTypeEditor';
import { DoorEditor } from './components/DoorEditor/DoorEditor';
import { ResetConfirmModal } from './components/EditorCommon/ResetConfirmModal';
import User from './models/User';
import { InteriorEditor } from './components/InteriorEditor/InteriorEditor';
import { CommonEditorProps, EditorContext } from './components/EditorCommon/EditorContext';
import { PieceListEditor } from './components/PieceListEditor/PieceListEditor';
import { LoginContext } from './components/EditorCommon/LoginContext';
import { ChainInfoService } from './services/ChainInfoService';
import { MaterialEditor } from './components/admin/MaterialEditor/MaterialEditor';
import { TranslationContext } from './contexts/TranslationContext';
import { CabinetList } from './components/CabinetList/CabinetList';
import { CustomerSearch } from './components/CustomerSearch/CustomerSearch';
import { ChainEditor } from './components/admin/ChainEditor/ChainEditor';
import { ChainCss } from './components/ChainCss';
import { ProductLineAdminEditor } from './components/admin/ProductLineAdminEditor/ProductLineAdminEditor';
import { ExtraProductEditor } from './components/ExtraProductEditor/ExtraProductEditor';
import { ProductAdminEditor } from './components/admin/ProductAdminEditor/ProductAdminEditor';
import { InteriorMaterialEditor } from './components/admin/InteriorMaterialEditor/InteriorMaterialEditor';
import { DoorAdmin } from './components/admin/DoorAdmin/DoorAdmin';
import { MiscAdmin } from './components/admin/Misc/MiscAdmin';
import { usePromise } from './components/EditorCommon/usePromise';
import { TranslationEditor } from './components/admin/TranslationEditor/TranslationEditor';
import { useClient } from './util/useClient';
import { useState2 } from './util/useState2';
import { TopNavContext } from './contexts/TopNavContext';
import { DebugContext, DebugContextProps } from './contexts/DebugContext';

interface ResetConfirmState {
    confirmer: ((a: boolean) => void),
    topics: ResetTopic[],
}

function App(props: {

}) {


    const history = useHistory();
    const [user, setUser] = useState<User | undefined>(undefined);
    const [resetConfirmState, setResetConfirmState] = useState<ResetConfirmState | undefined>();
    let [cabinet, setCabinet] = useState<ICabinet | undefined>();
    const showTranslationKeys = useState2(false);
    const debugContextProps = useState2<DebugContextProps>({
        displayExtendedProductInfo: false,
    });
    const translationClient = useClient(TranslationClient);
    const translations = usePromise(useCallback(async () => {
        const response = await translationClient.getTranslations();
        return response.translations;
    },
        [translationClient]
    ));

    const translationService = new TranslationService(translations, showTranslationKeys.value);
    const translator = translationService.translate.bind(translationService);

    const loginService = new LoginService(setUser);

    const resetConfirmer: ResetConfirmer = (topics) => new Promise((resolve, reject) => {
        //this is run if you make a change where the user needs to confirm that some changes to the cabinet are discarded.

        //if there is already a dialog open, discard that
        resetConfirmState?.confirmer(false);

        setResetConfirmState({
            topics,
            confirmer: (ans: boolean) => {
                //this is run when the user confirms or abandons their changes.
                setResetConfirmState(undefined);
                resolve(ans);
            }
        })
    });

    const chainInfo = usePromise(useCallback(
        async () => {
            const cis = new ChainInfoService(new ChainInfoClient());
            return await cis.getChainInfo();
        },
        []
    ));

    let cabinetService = undefined;
    let navService = undefined;
    if (chainInfo) {
        if (!cabinet) {
            cabinet = CabinetService.createDefaultCabinet(chainInfo);
            setCabinet(cabinet);
        }
        cabinetService = new CabinetService(chainInfo, resetConfirmer, cabinet, setCabinet);
        navService = new NavService(cabinetService, history);
    }



    let resetConfirmModal = resetConfirmState && (
        <ResetConfirmModal
            translator={translator}
            onAnswer={resetConfirmState.confirmer}
            topics={resetConfirmState.topics}
        />
    );

    let routeSwitch = (
        <>
            <ChainCss />
            <Switch >
                <Route path="/login">
                    <Login onLoginSuccess={() => history.push("/")} loginService={loginService} />
                </Route>

                { /* Editor Routes */}
                <Route exact path={NavPage.ProductLine.url} key="1">
                    <ProductLineEditor />
                </Route>

                <Route path={NavPage.Dimensions.url} key="2">
                    <DimensionsEditor />
                </Route>

                <Route path={NavPage.DoorTypes.url} key="4">
                    <DoorTypeEditor />
                </Route>

                <Route path={NavPage.Doors.url} key="5">
                    <DoorEditor />
                </Route>

                <Route path={NavPage.Interior.url} key="6">
                    <InteriorEditor />
                </Route>

                <Route path={NavPage.PieceList.url} key="7">
                    <PieceListEditor />
                </Route>

                { /* Admin Routes */}
                <Route path={NavPage.Admin.Material.url}>
                    <MaterialEditor />
                </Route>

                <Route path={NavPage.Admin.Chain.url}>
                    <ChainEditor />
                </Route>

                <Route path={NavPage.Admin.ProductLine.url}>
                    <ProductLineAdminEditor />
                </Route>

                <Route path={NavPage.Admin.Product.url}>
                    <ProductAdminEditor />
                </Route>

                <Route path={NavPage.Admin.InteriorMaterial.url}>
                    <InteriorMaterialEditor />
                </Route>

                <Route path={NavPage.Admin.Door.url}>
                    <DoorAdmin />
                </Route>

                <Route path={NavPage.Admin.Translation.url}>
                    <TranslationEditor showTranslationKeys={showTranslationKeys} />
                </Route>

                <Route path={NavPage.Admin.Misc.url}>
                    <MiscAdmin />
                </Route>

                <Route path={NavPage.User.CabinetList.url}>
                    <CabinetList />
                </Route>

                <Route path={NavPage.User.UserSearch.url}>
                    <CustomerSearch />
                </Route>

                <Route path={NavPage.ExtraProducts.url}>
                    <ExtraProductEditor />
                </Route>

                <Route>
                    <NotFound translator={translator} />
                </Route>
            </Switch>


        </>
    );

    if (cabinetService && cabinet && navService) {

        let commonEditorProps: CommonEditorProps = {
            navService: navService,
            cabinetService: cabinetService,
            cabinet: cabinet,
            translator: translator,
        };
        routeSwitch = (
            <EditorContext.Provider value={commonEditorProps}>
                {routeSwitch}
            </EditorContext.Provider>

        );
    }

    return (
        <div onDragOver={preventDrop}>
            <DebugContext.Provider value={debugContextProps}>
                <TranslationContext.Provider value={{ translator: translator }} >
                    <LoginContext.Provider value={{
                        loginService: loginService,
                    }}>


                        {resetConfirmModal}
                        <TopNavContext.Provider value={{
                            currentUser: user,
                            loginService: loginService,
                            translator: translator,
                            navService: navService,
                            logoUrl: chainInfo?.logoUrl
                        }}>

                            {routeSwitch}

                        </TopNavContext.Provider>
                    </LoginContext.Provider>
                </TranslationContext.Provider>
            </DebugContext.Provider>
        </div>
    );
}



function preventDrop(evt: React.DragEvent) {
    evt.preventDefault();
    evt.stopPropagation();
    evt.dataTransfer.dropEffect = "none";
}

export default App;
